import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef } from 'react';
import BetterScroll from '@better-scroll/core';
import ScrollBar, { ScrollbarOptions } from '@better-scroll/scroll-bar';
import Pullup from '@better-scroll/pull-up';
import { debounce } from 'lodash-es';
import { useLatest } from 'ahooks';

BetterScroll.use(Pullup);
BetterScroll.use(ScrollBar);

interface Props {
  className: string;
  children: React.ReactNode;
  scrollX?: boolean;
  scrollY?: boolean;
  autoRefresh?: boolean;
  onPullingUp?: VoidFunc;
  onScrollEvent?: VoidFunc;
  onRefresh?: VoidFunc;
  allowClick?: boolean;
  realTime?: boolean;
  onScrolling?: (lastBsInstance: ReturnType<typeof BetterScroll>) => void;
  scrollbar?: ScrollbarOptions;
}

export interface ScrollViewRefAttrs {
  bsInstance: ReturnType<typeof BetterScroll>;
  handleRefresh: VoidFunc;
}

const ScrollView = forwardRef<ScrollViewRefAttrs, Props>((props, ref) => {
  const {
    className,
    children,
    onPullingUp,
    allowClick = true,
    autoRefresh = false,
    scrollX = false,
    scrollY = true,
    onScrollEvent,
    onRefresh,
    onScrolling,
    realTime = false,
    scrollbar = false,
  } = props;
  const bsRef = useRef<ReturnType<typeof BetterScroll>>(null);
  const latestOnPullingUpRef = useLatest(onPullingUp);
  const latestScrollPositionRef = useLatest(onScrolling);

  const filteredClassname = useMemo(() => className?.split(' ')[0], [className]);

  useImperativeHandle(
    ref,
    () => ({
      bsInstance: bsRef.current,
      handleRefresh: () => !autoRefresh && setTimeout(() => bsRef.current?.refresh(), 350),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [bsRef.current, autoRefresh]
  );

  const debounceRefresh = useMemo(
    () =>
      debounce(() => {
        bsRef.current?.refresh();
      }, 350),
    []
  );

  const styleObj: React.CSSProperties = useMemo(
    () => ({
      overflow: 'hidden',
    }),
    []
  );

  useEffect(() => {
    bsRef.current = new BetterScroll(`.${filteredClassname}`, {
      scrollX,
      scrollY,
      probeType: realTime ? 1 : 0,
      momentumLimitTime: 50,
      bounceTime: 200,
      swipeBounceTime: 200,
      click: allowClick,
      momentumLimitDistance: 20,
      scrollbar,
      ...(!!onPullingUp ? { pullUpLoad: { threshold: 80 } } : {}),
    });
    // 只有实时派发才能使用scroll事件
    onScrolling &&
      realTime &&
      bsRef.current?.on('scroll', pos => {
        // 处理实时滚动事件的逻辑 获取最新的参数
        latestScrollPositionRef.current?.(pos);
      });

    onScrollEvent &&
      bsRef.current?.on('beforeScrollStart', () => {
        // 处理滚动事件的逻辑
        onScrollEvent();
      });

    onRefresh &&
      bsRef.current?.on('refresh', () => {
        // 处理滚动事件的逻辑
        onRefresh?.();
      });

    onPullingUp &&
      bsRef.current.on('pullingUp', () => {
        !autoRefresh && setTimeout(() => bsRef.current?.refresh(), 350);
        latestOnPullingUpRef.current?.();
      });

    return () => {
      bsRef.current.off();
      bsRef.current?.destroy?.();
      bsRef.current = null;
      latestOnPullingUpRef.current = null;
      latestScrollPositionRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (autoRefresh) {
      debounceRefresh();
    }
  });

  return (
    <div className={className} style={styleObj}>
      {children}
    </div>
  );
});

export default ScrollView;
